import { useRef, useState } from "react";

import { CommonDataSetExportApi } from "@/Administration/Api/CommonDataSetExport/CommonDataSetExportApi";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { Guid } from "@/Common/Models/Guid";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { Button, IconButton, LinkButton } from "@/DesignSystem";
import { ProviderSelection } from "./ProviderSelection";

import styles from "./OntarioCommonDataSetImportForm.module.scss";

import XRegularIcon from "@/DesignSystem/Atoms/Icons/Types/X/x-regular.svg";

function FormButtons(props: Readonly<{ onImport: () => void, onCancel: () => void, isFormValid: boolean, disabled: boolean }>): JSX.Element {
    const { getText } = useTranslation();

    return (
        <div className={styles.footerButtonsContainer}>
            <ButtonGroup>
                <Button
                    onClick={props.onCancel}
                    variant="neutral"
                    category="secondary"
                    dataCy="ontarioCommonDataSetImportForm_CancelButton"
                    label={getText("CancelLabel")}
                />
                <Button
                    onClick={props.onImport}
                    variant="brand"
                    category="primary"
                    dataCy="ontarioCommonDataSetImportForm_ImportButton"
                    label={getText("ImportLabel")}
                    disabled={!props.isFormValid || props.disabled}
                />
            </ButtonGroup>
        </div>
    );
}

function WarningPopupFormButtons(props: Readonly<{ onOk: () => void, onGoBack: () => void }>): JSX.Element {
    const { getText } = useTranslation();

    return (
        <div className={styles.footerButtonsContainer}>
            <ButtonGroup>
                <Button
                    onClick={props.onGoBack}
                    variant="neutral"
                    category="secondary"
                    dataCy="ontarioCommonDataSetImportForm_WarningPopup_BackButton"
                    label={getText("CommonDataSetImport_GoBackLabel")}
                />
                <Button
                    onClick={props.onOk}
                    variant="brand"
                    category="primary"
                    dataCy="ontarioCommonDataSetImportForm_WarningPopup_OkButton"
                    label={getText("OKLabel")}
                />
            </ButtonGroup>
        </div>
    );
}

function OntarioCommonDataSetImportForm(props: Readonly<{ onClose: () => void }>): JSX.Element {
    const { getText } = useTranslation();

    const [formState, setFormState] = useState<{ providerContactFacetId: Guid, file: File | null }>({
        providerContactFacetId: null,
        file: null
    });

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [showWarning, setShowWarning] = useState<boolean>(false);

    const uploadComponentRef = useRef<HTMLInputElement | null>(null);

    async function onImportButtonClick(): Promise<void> {
        setIsLoading(true);
        const response = await CommonDataSetExportApi.createCommonDataSetImport({ EmployeeId: formState.providerContactFacetId, File: formState.file });
        setIsLoading(false);
        if (!response.isSuccess) {
            if (response.errors.any(p => p.Code === "InvalidXmlFormat")) {
                setShowWarning(true);
            }
            else {
                sessionStore.showPopup(response.errors);
            }
            return;
        }
        props.onClose();
        sessionStore.dispatch(LocalOperations.AdministrationLog.RefreshGrid);
    }

    if (showWarning) {
        return (
            <Modal
                onClose={props.onClose}
                showX
                title={getText("WarningTitleLabel")}
                footer={(
                    <WarningPopupFormButtons
                        onOk={props.onClose}
                        onGoBack={() => {
                            setShowWarning(false);
                            setFormState(p => ({ ...p, file: null }));
                        }}
                    />
                )}
            >
                <div>
                    {getText("CommonDataSetImport_IncorrectFileFormatErrorMessage")}
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            onClose={props.onClose}
            showX
            width={600}
            title={getText("CommonDataSetImport_PopupTitle")}
            style={{ overflow: "visible" }}
            bodyStyle={{ overflow: "visible" }}
            footer={(
                <FormButtons
                    onCancel={props.onClose}
                    onImport={onImportButtonClick}
                    isFormValid={!GuidHelper.IsNullOrEmpty(formState.providerContactFacetId) && formState.file !== null}
                    disabled={isLoading}
                />
            )}
        >
            <div className={styles.cdsBody}>
                <ProviderSelection
                    providerContactFacetId={formState.providerContactFacetId}
                    onChange={providerContactFacetId => setFormState(p => ({ ...p, providerContactFacetId }))}
                />
                <div className={styles.fileUploadSection}>
                    {formState.file
                        ? (
                            <ButtonGroup>
                                <LinkButton
                                    label={getText("ImportFileLabel", { fileName: formState.file.name })}
                                    underline={false}
                                />
                                <IconButton
                                    variant="error"
                                    category="tertiary"
                                    size="sm"
                                    onClick={() => setFormState(p => ({ ...p, file: null }))}
                                    data-cy="ontarioCommonDataSetExportForm_DeselectFileButton"
                                >
                                    <XRegularIcon />
                                </IconButton>
                            </ButtonGroup>

                        )
                        :
                        (
                            <LinkButton
                                label={getText("CommonDataSetImport_SelectFileToImportLabel")}
                                underline={false}
                                onClick={() => uploadComponentRef.current?.click()}
                            />
                        )
                    }
                    <div style={{ display: "none" }}>
                        <input
                            ref={ref => uploadComponentRef.current = ref}
                            id="CDS-import-uploadinput"
                            data-cy="file-upload-input"
                            name="files"
                            type="file"
                            onChange={e => setFormState({ ...formState, file: e.target.files?.item(0) ?? null })}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export { OntarioCommonDataSetImportForm };
